import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiClient';
import toast from 'react-hot-toast';
import { 
  BellIcon, 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  InformationCircleIcon,
  UserPlusIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  CheckIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';

const NotificationsPage = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);

  // Set page title
  useEffect(() => {
    document.title = 'Notifications | Customer Portal';
  }, []);

  // Fetch notifications from API
  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // apiClient interceptor already extracts response.data.data
      const notificationData = await apiClient.get(`/notifications?filter=${filter}`);
      setNotifications(Array.isArray(notificationData) ? notificationData : []);
      
      // Fetch unread count
      const countData = await apiClient.get('/notifications/unread-count');
      setUnreadCount(countData.count || countData.unreadCount || 0);
    } catch (error) {
      console.error('[Notifications] Error fetching:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await apiClient.patch(`/notifications/${notificationId}/read`);
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true, readAt: new Date().toISOString() }
            : notification
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('[Notifications] Error marking as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.patch('/notifications/mark-all-read');
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('[Notifications] Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await apiClient.delete(`/notifications/${notificationId}`);
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      toast.success('Notification deleted');
    } catch (error) {
      console.error('[Notifications] Error deleting:', error);
      toast.error('Failed to delete notification');
    }
  };

  const getNotificationIcon = (type) => {
    const typeUpper = type?.toUpperCase();
    switch (typeUpper) {
      case 'SUCCESS':
      case 'LICENSE_APPROVED':
      case 'REQUEST_APPROVED':
      case 'ACTIVATION_KEY_APPROVED':
      case 'CLUSTER_LICENSE_APPROVED':
        return <CheckCircleIcon className="w-6 h-6 text-green-600" />;
      case 'WARNING':
      case 'LICENSE_REJECTED':
      case 'REQUEST_REJECTED':
      case 'ACTIVATION_KEY_REJECTED':
      case 'CLUSTER_LICENSE_REJECTED':
        return <ExclamationCircleIcon className="w-6 h-6 text-yellow-600" />;
      case 'ERROR':
        return <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />;
      case 'REQUEST_SUBMITTED':
        return <DocumentTextIcon className="w-6 h-6 text-purple-600" />;
      case 'EMAIL_SENT':
        return <CheckCircleIcon className="w-6 h-6 text-blue-600" />;
      case 'SYSTEM_UPDATE':
        return <ShieldCheckIcon className="w-6 h-6 text-gray-600" />;
      case 'INFO':
      default:
        return <InformationCircleIcon className="w-6 h-6 text-blue-600" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-l-4 border-red-500 bg-red-50';
      case 'medium':
        return 'border-l-4 border-yellow-500 bg-yellow-50';
      case 'low':
        return 'border-l-4 border-green-500 bg-green-50';
      default:
        return 'border-l-4 border-blue-500 bg-blue-50';
    }
  };

  const formatTimestamp = (date) => {
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    const minutes = Math.floor(diff / (1000 * 60));

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'ALL') return true;
    if (filter === 'UNREAD') return !notification.read;
    if (filter === 'READ') return notification.read;
    return notification.type === filter;
  });

  const getFilterOptions = () => {
    const isAdmin = user?.role === 'ADMIN';
    const baseOptions = [
      { value: 'ALL', label: 'All Notifications' },
      { value: 'UNREAD', label: 'Unread' },
      { value: 'READ', label: 'Read' }
    ];

    if (isAdmin) {
      return [
        ...baseOptions,
        { value: 'user_registration', label: 'User Registrations' },
        { value: 'license_request', label: 'License Requests' },
        { value: 'system_alert', label: 'System Alerts' }
      ];
    }

    return [
      ...baseOptions,
      { value: 'success', label: 'Approvals' },
      { value: 'info', label: 'Information' }
    ];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600 mt-1">
            Stay updated with the latest activities
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button 
            onClick={markAllAsRead}
            variant="outline"
            className="border-[#197560] text-[#197560] hover:bg-[#197560] hover:text-white"
          >
            <CheckIcon className="w-4 h-4 mr-2" />
            Mark All Read
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-wrap gap-2">
          {getFilterOptions().map(option => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === option.value
                  ? 'bg-[#197560] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {filteredNotifications.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No notifications</h3>
            <p className="mt-2 text-gray-500">
              {filter === 'UNREAD' 
                ? "You're all caught up! No unread notifications."
                : filter === 'ALL'
                ? "You don't have any notifications yet."
                : `No ${filter.toLowerCase()} notifications found.`}
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md ${
                !notification.read ? 'ring-2 ring-blue-100' : ''
              }`}
            >
              <div className={`p-6 ${notification.priority ? getPriorityColor(notification.priority) : ''}`}>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {notification.title}
                        </h3>
                        {!notification.read && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            New
                          </span>
                        )}
                        {notification.priority && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            notification.priority === 'high' ? 'bg-red-100 text-red-800' :
                            notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {notification.priority.charAt(0).toUpperCase() + notification.priority.slice(1)} Priority
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(notification.timestamp)}
                        </span>
                        <button
                          onClick={() => deleteNotification(notification.id)}
                          className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                          title="Delete notification"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                      {notification.message}
                    </p>
                    <div className="mt-4 flex items-center space-x-3">
                      {notification.actionUrl && (
                        <a
                          href={notification.actionUrl}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-[#197560] hover:bg-[#155a4a] transition-colors"
                        >
                          View Details
                        </a>
                      )}
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                        >
                          <CheckIcon className="w-3 h-3 mr-1" />
                          Mark as Read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary Stats for Admin */}
      {user?.role === 'ADMIN' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {notifications.filter(n => n.type === 'user_registration').length}
              </div>
              <div className="text-sm text-gray-600">User Registrations</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {notifications.filter(n => n.type === 'license_request').length}
              </div>
              <div className="text-sm text-gray-600">License Requests</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {notifications.filter(n => n.type === 'system_alert').length}
              </div>
              <div className="text-sm text-gray-600">System Alerts</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {notifications.filter(n => n.read).length}
              </div>
              <div className="text-sm text-gray-600">Processed</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;